// src/pages/MyAccount/ChangePasswordModal.jsx
import React, { useMemo } from "react";
import { CheckCircle2, Circle, Save } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { api } from "../../services/apiService";
import { clearAuthData } from "../../utils/tokenUtils";

const computeStrength = (pw) => {
  if (!pw) return 0;
  const lengthScore = Math.min(16, pw.length) / 16;
  const hasUpper = /[A-Z]/.test(pw);
  const hasLower = /[a-z]/.test(pw);
  const hasNum = /[0-9]/.test(pw);
  const hasSym = /[^A-Za-z0-9]/.test(pw);
  const variety =
    [hasUpper, hasLower, hasNum, hasSym].filter(Boolean).length / 4;
  return Math.round((lengthScore * 0.4 + variety * 0.6) * 100);
};

const strengthLabel = (score) => {
  if (score < 30) return "Very Weak";
  if (score < 50) return "Weak";
  if (score < 70) return "Fair";
  if (score < 85) return "Good";
  return "Strong";
};

const strengthColorClass = (score) => {
  if (score < 30) return "bg-red-500";
  if (score < 50) return "bg-yellow-500";
  if (score < 70) return "bg-blue-500";
  if (score < 85) return "bg-primary-600";
  return "bg-green-500";
};

const ChangePasswordModal = ({
  open,
  onOpenChange,
  setConfirmModalOpen,
  form,
  setForm,
  rules,
  strength,
  serverError,
  setServerError,
}) => {
  const passwordsMatch =
    form.new_password && form.new_password === form.confirm_password;

  const allRulesPassed = useMemo(
    () => Object.values(rules).every(Boolean),
    [rules],
  );

  // New modal for confirmaton message
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setServerError("");
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-lg max-h-[90vh] overflow-y-auto sm:max-w-lg"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-text-primary">
              Change Password
            </DialogTitle>
          </DialogHeader>

          <form
            className="space-y-4"
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
          >
            <div>
              <Label htmlFor="modal_current_password">Current Password</Label>
              <Input
                id="modal_current_password"
                name="current_password"
                type="password"
                value={form.current_password}
                onChange={handleChange}
                autoComplete="off"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="modal_new_password">New Password</Label>
              <Input
                id="modal_new_password"
                name="new_password"
                type="password"
                value={form.new_password}
                onChange={handleChange}
                autoComplete="new-password"
                required
                className="mt-1"
              />
              <p className="text-xs text-text-tertiary mt-1">
                Use at least 8 characters with uppercase, number, and symbol
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${strengthColorClass(strength)}`}
                    style={{ width: `${strength}%` }}
                  />
                </div>
              </div>
              <p className="min-w-[80px] text-right text-sm font-bold text-text-primary">
                {strengthLabel(strength)}
              </p>
            </div>

            <div className="space-y-2">
              {[
                { label: "At least 8 characters", ok: rules.minLength },
                {
                  label: "Contains uppercase letter (A-Z)",
                  ok: rules.uppercase,
                },
                { label: "Contains number (0-9)", ok: rules.number },
                { label: "Contains symbol (e.g. !@#$%)", ok: rules.symbol },
              ].map((r, i) => (
                <div key={i} className="flex items-center gap-2">
                  {r.ok ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-sm text-text-primary">{r.label}</span>
                </div>
              ))}
            </div>

            <div>
              <Label htmlFor="modal_confirm_password">
                Confirm New Password
              </Label>
              <Input
                id="modal_confirm_password"
                name="confirm_password"
                type="password"
                value={form.confirm_password}
                onChange={handleChange}
                autoComplete="new-password"
                required
                className={`mt-1 ${form.confirm_password && !passwordsMatch ? "border-red-500" : ""}`}
              />
              {form.confirm_password && !passwordsMatch && (
                <p className="text-xs text-red-500 mt-1">
                  Passwords do not match
                </p>
              )}
            </div>

            {serverError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{serverError}</p>
              </div>
            )}

            <Separator />

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  setConfirmModalOpen(true);
                }}
                disabled={!allRulesPassed || !passwordsMatch}
                className="bg-primary-600 hover:bg-primary-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ChangePasswordModal;
